import { PrototypeApiError } from '../apiClient';
import type { BillingCycle } from '../prototypeTypes';

const cycleLabel: Record<BillingCycle, string> = {
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	annual: 'Annual',
};

export function checkoutErrorMessage(error: unknown, selectedCycle: BillingCycle): string {
	if (!(error instanceof PrototypeApiError)) return 'We could not start checkout. Please try again.';
	const body = asBody(error.body);
	switch (body.error) {
		case 'checkout_already_in_progress': return 'A checkout is already in progress. Please finish it or wait a moment before trying again.';
		case 'checkout_confirmation_pending': return `Your checkout${body.orderId ? ` (${body.orderId})` : ''} is awaiting confirmation. Please wait a moment, then try again.`;
		case 'billing_cycle_unavailable': return `${cycleLabel[selectedCycle]} billing is not available for this plan right now. Choose another billing cycle.`;
		case 'active_subscription_exists': return 'You already have a paid subscription. Use Upgrade or Membership & Billing instead of starting another subscription.';
		case 'subscription_reconciliation_required': return 'We need to reconcile your billing status before starting checkout. Refresh Membership & Billing or contact support.';
		case 'subscription_resume_required': return 'Resume auto-renew in Membership & Billing before upgrading. No charge was made.';
		case 'payment_recovery_required': return 'Please resolve the payment issue in Membership & Billing before upgrading. No charge was made.';
		case 'upgrade_already_in_progress': return 'An upgrade is already in progress. Please wait a moment, then try again.';
		case 'subscription_state_changed': return 'Your subscription changed while upgrading. Please check Membership & Billing and try again.';
		case 'provider_upgrade_rejected': return 'Your upgrade could not be completed. No charge was made. Please try again or contact support.';
		default:
			if (error.status === 503) return 'Checkout status is temporarily unavailable. Please try again in a moment.';
			return 'We could not start checkout. Please try again.';
	}
}

/**
 * Whether an upgrade/checkout was blocked because auto-renew is off and must be
 * re-enabled before Creem will process the upgrade (subscription_resume_required).
 * The client surfaces a dedicated "re-open auto-renew" prompt for this case.
 */
export function isUpgradeResumeRequired(error: unknown): boolean {
	if (!(error instanceof PrototypeApiError)) return false;
	return asBody(error.body).error === 'subscription_resume_required';
}

function asBody(value: unknown): { error?: string; orderId?: string } {
	return value && typeof value === 'object' ? value as { error?: string; orderId?: string } : {};
}
